import { useAppContext } from "@ftdr/blueprint-components-react";

import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { SetupIntentResult } from "@stripe/stripe-js";
import { useCallback } from "react";
import { usePaymentContext } from "../../../../../../contexts/payment-context";
import {
  setError,
  usePaymentsClientContext,
} from "../../../../../../contexts/payments-client-context";
import {
  AgregatedProcessPaymentResponse,
  FailureTypeValue,
  GeneralStatusValue,
} from "../../../../types";
import { StripeFormDetails } from "../../stripe-form";
import { useStripeElementsValidations } from "./use-stripe-elements-validation";

type ConfirmCardSetupReturn = {
  result?: { isValid: boolean; confirmCardSetupResult: SetupIntentResult };
  error?: AgregatedProcessPaymentResponse;
};

type ConfirmCardSetupType = (
  formData: StripeFormDetails,
  clientSecret: string
) => Promise<ConfirmCardSetupReturn>;

type UseConfirmCardSetupReturnValues = {
  confirmCardSetup: ConfirmCardSetupType;
};

export const useConfirmCardSetup = (): UseConfirmCardSetupReturnValues => {
  const {
    appSettings: { localizedText },
  } = useAppContext();
  const { onFailure } = usePaymentContext();
  const { dispatch } = usePaymentsClientContext();
  const stripe = useStripe();
  const elements = useElements();
  const { handleValidationResult } = useStripeElementsValidations();

  const confirmCardSetup = useCallback<ConfirmCardSetupType>(
    async (
      {
        city,
        country,
        postal_code,
        state,
        line1,
        line2,
        email,
        name,
        phone,
      }: StripeFormDetails,
      clientSecret: string
    ) => {
      const cardElement = elements?.getElement(CardNumberElement);
      // Initial checks if stripe elements nad cardElement is exist
      if (!stripe || !elements || !cardElement) {
        dispatch(setError({ message: localizedText(`STRIPE_LOAD_ERROR`) }));
        const errorResult = {
          type: FailureTypeValue.STRIPE_FAILURE,
          stripeFailureResponse: {
            message: localizedText(`STRIPE_LOAD_ERROR`),
          },
        };
        onFailure(errorResult);
        // If any of the errors occurs it will return error immediatly
        return {
          error: {
            stripe: { ...errorResult },
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          },
        };
      }

      try {
        // STEP 1 Confirm Card Setup by using stripe cardNumber element
        const confirmCardSetupResult = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                ...(name ? { name } : {}),
                ...(phone ? { phone } : {}),
                ...(email ? { email } : {}),
                address: {
                  ...(city ? { city } : {}),
                  ...(postal_code ? { postal_code } : {}),
                  ...(state ? { state } : {}),
                  ...(line1 ? { line1 } : {}),
                  ...(line2 ? { line2 } : {}),
                  ...(country ? { country } : {}),
                },
              },
            },
          }
        );
        const isValid = handleValidationResult(confirmCardSetupResult);
        if (isValid) {
          return {
            result: {
              isValid,
              confirmCardSetupResult,
            },
          };
        } else {
          return {
            error: {
              stripe: {
                type: FailureTypeValue.STRIPE_FAILURE,
                stripeFailureResponse: {
                  message: confirmCardSetupResult?.error?.message,
                  rawError: confirmCardSetupResult,
                },
              },
              generalStatus: GeneralStatusValue.PAYMENT_FAILED,
            },
          };
        }
      } catch (e) {
        // Return Error If Confirm Card Setup  call was unsuccessfull
        const errorResult = {
          type: FailureTypeValue.STRIPE_FAILURE,
          stripeFailureResponse: {
            message: localizedText(`STRIPE_UNKNOWN_ERROR`),
          },
        };
        onFailure(errorResult);
        dispatch(setError({ message: localizedText(`STRIPE_UNKNOWN_ERROR`) }));
        return {
          error: {
            stripe: {
              ...errorResult,
            },
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          },
        };
      }
    },
    [
      dispatch,
      elements,
      localizedText,
      onFailure,
      stripe,
      handleValidationResult,
    ]
  );

  return { confirmCardSetup };
};
